import ApiService from "./ApiService";
import { hasServerAnswer } from "./hasServerAnswer";

interface SignInData {
  email: string;
  password: string;
}

/**
 * When the sign-in request gets NO response at all (network error, DNS failure, timeout), try again after about 1 s, then about
 * 2 s. Logging in twice is harmless, so this endpoint, and only this one, may repeat a POST that might have reached the server.
 * Any answer from the server (whatever the status) ends it at once. "Answer" means a real status: axios 0.27 puts the bare
 * XMLHttpRequest (status 0) in `error.response` when there is no network, and that is NOT an answer (DW-026).
 */
export const SIGN_IN_RETRY_DELAYS_MS = [1000, 2000];

const wait = (ms: number) => new Promise<void>((resolve) => setTimeout(resolve, ms));

export async function apiSignIn(data: SignInData) {
  for (let attempt = 0; ; attempt += 1) {
    try {
      const res = await ApiService.fetchData({
        url: "/admin/login",
        method: "post",
        data,
        // The sign-in form shows its own sentence; no "Something went wrong" toast on top.
        handlesOwnError: true,
        // A try that hangs should not hold the form for a minute.
        timeout: 20000,
      });
      return res?.data;
    } catch (error: any) {
      if (hasServerAnswer(error) || attempt >= SIGN_IN_RETRY_DELAYS_MS.length) throw error;
      await wait(SIGN_IN_RETRY_DELAYS_MS[attempt]);
    }
  }
}

export async function apiSignUp(data: any) {
  return ApiService.fetchData({
    url: "/sign-up",
    method: "post",
    data,
  });
}

export async function apiSignOut(data?: any) {
  return ApiService.fetchData({
    url: "/admin/logout",
    method: "post",
    data,
  });
}

export async function apiForgotPassword(data: { email: string }) {
  return ApiService.fetchData({
    url: "/api/forgotPassword",
    method: "post",
    data,
  });
}

export async function apiResetPassword(data: { password: string; token: string }) {
  return ApiService.fetchData({
    url: "/reset-password",
    method: "post",
    data,
  });
}
